import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ShieldCheck,
  MapPin,
  Lock,
  FileText,
  Users,
  Clock,
  CheckCircle2,
  ArrowLeft,
  Server,
  Eye,
  Database,
} from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { PublicLangToggle } from "../components/PublicLangToggle";

/**
 * Public-facing security & compliance page — sales tool for B2B prospects.
 * Loi 25 (Québec privacy law) is the killer feature for Québec advisory firms.
 */
export default function SecurityCompliancePage() {
  const { tr } = useLang();
  const navigate = useNavigate();

  const pillars = [
    {
      icon: MapPin,
      title: tr("Données 100 % au Canada", "100% of data in Canada"),
      desc: tr("MongoDB et stockage objet hébergés à Montréal (OVH BHS). Aucune donnée client ne quitte le territoire canadien — conforme à la Loi 25 du Québec et aux exigences de l'AMF.", "MongoDB and object storage hosted in Montreal (OVH BHS). No client data leaves Canadian soil — compliant with Quebec's Law 25 and AMF requirements."),
      proof: tr("Audit en temps réel via tableau de bord interne.", "Audited live from an internal dashboard."),
    },
    {
      icon: Lock,
      title: tr("Isolation multi-locataire garantie", "Guaranteed tenant isolation"),
      desc: tr("Garde-fou applicatif (`tenant_guard`) qui bloque chaque requête sans `org_id` valide. Une firme A ne peut techniquement pas accéder aux données d'une firme B — pas même par accident.", "An application guard (`tenant_guard`) blocks every request without a valid `org_id`. Firm A technically cannot reach firm B's data — not even by accident."),
      proof: tr("Test inter-organisations automatisé, exécuté à chaque déploiement.", "Automated cross-org test, run on every deployment."),
    },
    {
      icon: FileText,
      title: tr("Journal d'audit complet", "Complete audit trail"),
      desc: tr("Chaque accès, modification, suppression de donnée est journalisé avec horodatage, identité, IP source. Exportable pour vos auditeurs (cabinet comptable, AMF).", "Every access, change and deletion is logged with a timestamp, an identity and a source IP. Exportable for your auditors (accounting firm, AMF)."),
      proof: tr("Journaux conservés 7 ans (politique réglable par firme).", "Logs kept for 7 years (policy adjustable per firm)."),
    },
    {
      icon: Users,
      title: tr("Permissions fines", "Fine-grained permissions"),
      desc: tr("5 rôles hiérarchiques (président → PDG → VP → admin → employé). Chaque fonction, chaque donnée, soumise au contrôle d'accès. Le directeur de FLHQ ne peut pas voir les Q&R d'une autre firme cliente.", "5 hierarchical roles (president → CEO → VP → admin → employee). Every feature and every piece of data is behind access control. The FLHQ director cannot see another client firm's Q&A."),
      proof: tr("Contrôle côté serveur ET côté navigateur (défense en profondeur).", "Checked on the server AND in the browser (defence in depth)."),
    },
    {
      icon: Clock,
      title: tr("Sauvegardes automatiques", "Automatic backups"),
      desc: tr("Instantanés quotidiens chez OVH (conservés 30 jours), restauration testée chaque mois. RPO < 24 h, RTO < 4 h.", "Daily snapshots at OVH (kept 30 days), restore tested every month. RPO < 24 h, RTO < 4 h."),
      proof: tr("Restauration à un instant précis (PITR) offerte en Enterprise.", "Point-in-time recovery available on Enterprise."),
    },
    {
      icon: Eye,
      title: tr("Confidentialité IA", "AI confidentiality"),
      desc: tr("Vos vidéos, vos transcriptions, vos Q&R ne servent JAMAIS à entraîner des modèles tiers. OpenAI, Anthropic et Gemini reçoivent vos données le temps d'un appel seulement, sans conservation.", "Your videos, transcripts and Q&A are NEVER used to train third-party models. OpenAI, Anthropic and Gemini receive your data for the duration of one call only, with no retention."),
      proof: tr("Validé contractuellement avec OpenAI Business et AssemblyAI.", "Contractually confirmed with OpenAI Business and AssemblyAI."),
    },
  ];

  const certifications = [
    { name: tr("Loi 25 (Québec)", "Law 25 (Quebec)"), status: tr("Conforme — en vigueur depuis 2024", "Compliant — in force since 2024") },
    { name: tr("AMF — Cabinet de services financiers", "AMF — Financial services firm"), status: tr("Architecture compatible (audit annuel disponible)", "Compatible architecture (annual audit available)") },
    { name: tr("Équivalent RGPD", "GDPR-equivalent"), status: tr("Loi 25 reconnue par la Commission européenne", "Law 25 recognised by the European Commission") },
    { name: "SOC 2 Type II", status: tr("Prévu en 2026 (Enterprise)", "Planned for 2026 (Enterprise)") },
    { name: "ISO 27001", status: tr("Prévu en 2027", "Planned for 2027") },
  ];

  return (
    <div data-testid="page-security-compliance" className="min-h-screen bg-[#0a4d3a] text-white">
      <div className="max-w-6xl mx-auto px-6 py-12 lg:py-20">
        <Button
          data-testid="security-back-btn"
          onClick={() => navigate(-1)}
          variant="outline"
          className="bg-transparent border-white/30 text-white hover:bg-white/10 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {tr("Retour", "Back")}
        </Button>

        <div className="flex justify-end mb-4">
          <PublicLangToggle tone="dark" />
        </div>

        <div className="mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#c89b3c]/20 border border-[#c89b3c]/40 text-[#c89b3c] text-xs uppercase tracking-wider mb-6">
            <ShieldCheck className="w-3.5 h-3.5" />
            {tr("Sécurité & Conformité", "Security & Compliance")}
          </div>
          <h1 className="text-4xl lg:text-6xl font-serif mb-6 leading-tight">
            {tr("La conformité est ", "Compliance is ")}<span className="text-[#c89b3c]">{tr("non négociable", "non-negotiable")}</span>{tr(" dans la finance.", " in finance.")}
          </h1>
          <p className="text-xl text-white/80 max-w-3xl leading-relaxed">
            {tr(
              "Knoria a été conçu dès le premier jour pour respecter la Loi 25 du Québec et les exigences de l\u2019AMF. 100 % des données — vidéos, transcriptions, base de connaissances — résident au Canada, sur une infrastructure auditable.",
              "Knoria was built from day one to meet Quebec\u2019s Law 25 and the AMF requirements. 100% of the data — videos, transcripts, knowledge base — lives in Canada, on auditable infrastructure.",
            )}
          </p>
        </div>

        {/* Quick proofs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {[
            { kpi: "100%", label: tr("Données au Canada", "Data in Canada") },
            { kpi: "0", label: tr("Fuite entre organisations détectée", "Cross-tenant leaks found") },
            { kpi: "< 35ms", label: tr("Latence de la base à Montréal", "Database latency in Montreal") },
            { kpi: "24/7", label: tr("Journaux d\u2019audit disponibles", "Audit logs available") },
          ].map((s) => (
            <div
              key={s.label}
              className="border border-white/10 bg-white/5 backdrop-blur rounded-lg p-5"
            >
              <div className="text-3xl font-serif text-[#c89b3c] mb-1">{s.kpi}</div>
              <div className="text-sm text-white/70">{s.label}</div>
            </div>
          ))}
        </div>

        {/* 6 pillars */}
        <h2 className="text-2xl font-serif mb-8 text-white">{tr("Six garanties techniques", "Six technical guarantees")}</h2>
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          {pillars.map((p) => (
            <div
              key={p.title}
              data-testid={`pillar-${p.title.split(" ")[0].toLowerCase()}`}
              className="border border-white/10 bg-white/5 backdrop-blur rounded-lg p-6 hover:border-[#c89b3c]/40 transition-colors"
            >
              <p.icon className="w-8 h-8 text-[#c89b3c] mb-3" />
              <h3 className="text-lg font-semibold mb-2">{p.title}</h3>
              <p className="text-white/75 text-sm leading-relaxed mb-3">{p.desc}</p>
              <div className="text-xs text-[#c89b3c]/90 italic">→ {p.proof}</div>
            </div>
          ))}
        </div>

        {/* Architecture diagram (text) */}
        <div className="border border-white/10 bg-white/5 backdrop-blur rounded-lg p-8 mb-16">
          <h2 className="text-2xl font-serif mb-2">{tr("Architecture en bref", "Architecture at a glance")}</h2>
          <p className="text-white/70 mb-6">
            {tr("Trois couches, une seule frontière : le territoire canadien.", "Three layers, one border: Canadian soil.")}
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <Server className="w-7 h-7 text-[#c89b3c] mb-2" />
              <div className="font-semibold mb-1">{tr("Application", "Application")}</div>
              <div className="text-sm text-white/70">
                {tr("FastAPI + React. Hébergée sur une infrastructure conteneurisée auditable.", "FastAPI + React. Hosted on auditable containerised infrastructure.")}
              </div>
            </div>
            <div>
              <Database className="w-7 h-7 text-[#c89b3c] mb-2" />
              <div className="font-semibold mb-1">{tr("Base de données", "Database")}</div>
              <div className="text-sm text-white/70">
                {tr("MongoDB OVH région BHS (Beauharnois, Québec). Réplication avec bascule automatique.", "OVH MongoDB, BHS region (Beauharnois, Quebec). Replicated with automatic failover.")}
              </div>
            </div>
            <div>
              <MapPin className="w-7 h-7 text-[#c89b3c] mb-2" />
              <div className="font-semibold mb-1">{tr("Stockage objet", "Object storage")}</div>
              <div className="text-sm text-white/70">
                {tr("Vidéos et audios chiffrés au repos. OVH S3 BHS Montréal exclusivement.", "Videos and audio encrypted at rest. OVH S3 BHS Montreal only.")}
              </div>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <h2 className="text-2xl font-serif mb-6">{tr("Certifications & conformités", "Certifications & compliance")}</h2>
        <div className="border border-white/10 bg-white/5 backdrop-blur rounded-lg overflow-hidden mb-16">
          {certifications.map((c, i) => (
            <div
              key={c.name}
              className={`flex items-center justify-between px-6 py-4 ${
                i > 0 ? "border-t border-white/10" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#c89b3c]" />
                <span className="font-medium">{c.name}</span>
              </div>
              <span className="text-sm text-white/70">{c.status}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-[#c89b3c]/10 border border-[#c89b3c]/40 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-serif mb-3">
            {tr("Vous évaluez Knoria pour votre cabinet ?", "Evaluating Knoria for your firm?")}
          </h2>
          <p className="text-white/80 mb-6 max-w-2xl mx-auto">
            {tr(
              "Nous fournissons aux directions et aux auditeurs un dossier technique complet (architecture, journaux d\u2019audit, entente de traitement Loi 25). Demandez-le.",
              "We give leadership teams and auditors a complete technical dossier (architecture, audit logs, Law 25 data processing agreement). Just ask.",
            )}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              data-testid="security-pricing-btn"
              onClick={() => navigate("/pricing")}
              className="bg-[#c89b3c] hover:bg-[#b8893c] text-[#0a4d3a]"
            >
              {tr("Voir les forfaits", "See the plans")}
            </Button>
            <a href="mailto:contact@knoria.com?subject=Dossier%20s%C3%A9curit%C3%A9%20Knoria">
              <Button
                variant="outline"
                className="bg-transparent border-white/30 text-white hover:bg-white/10"
              >
                {tr("Demander le dossier complet", "Request the full dossier")}
              </Button>
            </a>
          </div>
        </div>

        <div className="text-center text-white/40 text-xs mt-12">
          {tr("Dernière mise à jour : 27 avril 2026 — version ", "Last updated: 27 April 2026 — version ")}{process.env.REACT_APP_VERSION || "current"}
        </div>
      </div>
    </div>
  );
}
